export * from "./presentation.js";
export * from "./progress.js";
export * from "./model.js";
export * from "./summary.js";
export * from "./projection.js";
export * from "./replay.js";
export * from "./speech.js";
export * from "./channel.js";

import type { Card, ComboType, HandResult, Rank, Seat, Team } from "@guandan/engine";
import type { SpeechCue, VoiceId } from "./speech.js";

export const PROTOCOL_VERSION = 5;

export type TablePhase = "playing" | "returnTribute" | "handOver" | "matchOver";

/**
 * The only reactions a client may send. Stable semantic IDs keep the wire
 * independent of an asset library and prevent arbitrary user-provided emoji.
 */
export const REACTION_IDS = [
  "steady",
  "clap",
  "laugh",
  "mindBlown",
  "joker",
  "thinking",
  "sweat",
  "cry",
  "party",
] as const;
export type ReactionId = (typeof REACTION_IDS)[number];

const reactionIdSet = new Set<string>(REACTION_IDS);
export function isReactionId(value: unknown): value is ReactionId {
  return typeof value === "string" && reactionIdSet.has(value);
}

/** A live-only table reaction. It is deliberately absent from snapshots and replays. */
export interface ReactionEvent {
  readonly id: string;
  readonly seat: Seat;
  readonly reactionId: ReactionId;
  readonly serverTime: number;
}

/** A speech cue placed on the same authoritative server clock as game events. */
export interface ScheduledSpeech {
  readonly startAt: number;
  readonly endAt: number;
  readonly cue: SpeechCue;
}

/** A combo as it crosses the wire — no engine internals. */
export interface WireCombo {
  readonly type: ComboType;
  readonly cards: readonly Card[];
  readonly rank: number;
  readonly length: number;
  readonly power: number;
  /** Plain-English name, so the client never has to reimplement the rules. */
  readonly label: string;
}

export interface SeatInfo {
  readonly seat: Seat;
  readonly userId: string;
  readonly name: string;
  readonly avatarId: string;
  readonly level: number;
  readonly rating: number;
  /** Deliberately absent from the wire: whether the player is an AI. */
  readonly connected: boolean;
  /** The seat is being played automatically after repeated timeouts. */
  readonly autoPlaying: boolean;
  /** Stable per-match voice selected by the authoritative GameRoom DO. */
  readonly voiceId: VoiceId;
}

export interface RoomInfo {
  readonly lifecycle?: "WAITING_FOR_HUMAN" | "ACTIVE" | "PAUSED" | "ABORTED";
  readonly pauseReason?: string | null;
  readonly resumeUntil?: number | null;
  readonly roomId: string;
  readonly rulesetId: string;
  readonly rulesetLabel: string;
  readonly turnMs: number;
  readonly coinTier: string;
  readonly entryCoins: number;
  readonly seats: readonly SeatInfo[];
}

/**
 * What a client is told about the game.
 *
 * Every field here is derived from the canonical event log by a pure per-seat
 * projection, so two clients in the same seat always see byte-identical data
 * and no seat can ever learn another's cards.
 */
export type WireEventBody =
  | {
      type: "handStarted";
      handNumber: number;
      level: Rank;
      playingTeam: Team;
      levels: readonly [Rank, Rank];
    }
  /** `hand` is the recipient's own cards. Everyone else is a count. */
  | { type: "dealt"; hand: readonly Card[]; counts: readonly number[] }
  | { type: "tributeResisted"; seats: readonly Seat[] }
  /** Tribute is played face up, so the card is public. */
  | { type: "tributePaid"; from: Seat; to: Seat; card: Card }
  /** The return is private between the two players; `card` is null to everyone else. */
  | { type: "tributeReturned"; from: Seat; to: Seat; card: Card | null }
  | {
      type: "turn";
      seat: Seat;
      /**
       * What the seat on turn owes. During `returnTribute` it is a card back to
       * the player who paid them, not a play — the seat alone does not say which.
       */
      phase: "playing" | "returnTribute";
      /** True when the seat may lead anything. */
      open: boolean;
      /** Absolute server time. The client renders it against a synced clock. */
      deadlineAt: number | null;
      /** Earliest authoritative server time at which this turn may be acted on. */
      turnReadyAt: number;
      /** Echoed back with an intent; a stale token is rejected. */
      turnToken: string;
      /** Sent early so clients can preload; playback still starts at `startAt`. */
      pendingSpeech?: ScheduledSpeech;
    }
  | { type: "played"; seat: Seat; cards: readonly Card[]; combo: WireCombo; remaining: number }
  | { type: "passed"; seat: Seat }
  | { type: "trickEnded"; winner: Seat; leader: Seat; jiefeng: boolean }
  | { type: "playerOut"; seat: Seat; place: number }
  | { type: "handEnded"; result: HandResult }
  | { type: "matchEnded"; winner: Team; summary?: import("./summary.js").MatchSummary };

/**
 * One event, stamped with when to *render* it.
 *
 * The server resolves a whole trick in well under a millisecond — three AI
 * passes are instantaneous — while the client needs seconds to show it. Rather
 * than let each client invent its own pacing, timing is part of the protocol:
 * `offset` is how long to wait after the previous event before showing this
 * one, `hold` is how long to dwell on it. AI think time is stamped here too, so
 * every client in the room paces a move identically.
 */
export interface WireEvent {
  readonly seq: number;
  /** Server time at which the authoritative state actually changed. */
  readonly t: number;
  /** Absolute server time at which every live client should present the event. */
  readonly presentAt: number;
  /** Absolute time after the event's visual/TTS dwell has completed. */
  readonly readyAt: number;
  /** Legacy/replay visual timing. Live clients do not derive timing from it. */
  readonly offset: number;
  readonly hold: number;
  readonly body: WireEventBody;
  /** Optional authoritative speech cues attached to this canonical event. */
  readonly presentation?: import("./presentation.js").Presentation;
  readonly speech?: readonly SpeechCue[];
}

/** Whose turn it is and what they owe. Current position, not a transition. */
export interface Turn {
  readonly seat: Seat;
  readonly phase: TablePhase;
  readonly open: boolean;
  readonly deadlineAt: number | null;
  readonly turnReadyAt: number;
  readonly turnToken: string;
  readonly pendingSpeech?: ScheduledSpeech;
}

/** One public play/pass entry in the current hand's table log. */
export interface TableAction {
  readonly seq: number;
  /** One-based trick number within the current hand. */
  readonly trick: number;
  readonly seat: Seat;
  readonly kind: "played" | "passed";
  /** Present only for a play; passes deliberately carry no cards. */
  readonly combo: WireCombo | null;
}

/** Enough to render the table from cold, after a reconnect or a long gap. */
export interface Snapshot {
  readonly lastResult?: HandResult | null;
  readonly seq: number;
  readonly phase: TablePhase;
  readonly handNumber: number;
  readonly level: Rank;
  readonly playingTeam: Team;
  readonly levels: readonly [Rank, Rank];
  readonly hand: readonly Card[];
  readonly counts: readonly number[];
  readonly out: readonly boolean[];
  readonly finished: readonly Seat[];
  readonly discard: readonly Card[];
  readonly turn: Seat;
  readonly open: boolean;
  readonly deadlineAt: number | null;
  readonly turnReadyAt: number;
  readonly turnToken: string;
  readonly pendingSpeech?: ScheduledSpeech;
  readonly current: { seat: Seat; combo: WireCombo } | null;
  /** Optional for compatibility with snapshots created before table history. */
  readonly trickNumber?: number;
  readonly trickActions?: readonly TableAction[];
  readonly lastTrickActions?: readonly TableAction[];
  readonly activity?: readonly TableAction[];
  readonly summary?: import("./summary.js").MatchSummary;
}

/** Actions carry no seat: the server takes it from the socket's identity. */
export type WireAction =
  | { type: "play"; cards: readonly Card[] }
  | { type: "pass" }
  | { type: "returnTribute"; card: Card };

export type HintReason =
  | "go-out"
  | "help-partner"
  | "preserve-bomb"
  | "take-control"
  | "shed-cards"
  | "low-cost"
  | "pass-partner"
  | "save-strength";

export interface HintSuggestion {
  readonly action: WireAction;
  readonly combo: WireCombo | null;
  readonly reasonKey: HintReason;
}

export type ClientMessage =
  | {
      kind: "intent";
      /** Idempotency key. Replaying an id returns the original outcome. */
      id: string;
      /** The seq the client believes is current; a mismatch triggers a resync. */
      expectSeq: number;
      /** From the most recent `turn` event. Rejects action on a stale turn. */
      turnToken: string;
      action: WireAction;
    }
  | { kind: "hint"; expectSeq: number; turnToken: string }
  | { kind: "reaction"; reactionId: ReactionId }
  | { kind: "resync"; since: number }
  | { kind: "ping"; t: number };

/**
 * Validate an untrusted WebSocket payload before the room reads fields from it.
 *
 * TypeScript types disappear on the wire. Keeping this parser beside the wire
 * type prevents a malformed JSON object from throwing inside a Durable Object
 * handler and makes the size limit on a selected play explicit.
 */
export function parseClientMessage(value: unknown): ClientMessage | null {
  if (typeof value !== "object" || value === null) return null;
  const message = value as Record<string, unknown>;
  if (message.kind === "ping") {
    return typeof message.t === "number" && Number.isFinite(message.t)
      ? { kind: "ping", t: message.t }
      : null;
  }
  if (message.kind === "resync") {
    return typeof message.since === "number" && Number.isSafeInteger(message.since) && message.since >= 0
      ? { kind: "resync", since: message.since }
      : null;
  }
  if (message.kind === "hint") {
    return typeof message.expectSeq === "number" && Number.isSafeInteger(message.expectSeq) && message.expectSeq >= 0 &&
      typeof message.turnToken === "string" && message.turnToken.length <= 128
      ? { kind: "hint", expectSeq: message.expectSeq, turnToken: message.turnToken }
      : null;
  }
  if (message.kind === "reaction") {
    return isReactionId(message.reactionId)
      ? { kind: "reaction", reactionId: message.reactionId }
      : null;
  }
  if (message.kind !== "intent" || typeof message.id !== "string" || message.id.length < 1 || message.id.length > 128 ||
      typeof message.expectSeq !== "number" || !Number.isSafeInteger(message.expectSeq) || message.expectSeq < 0 ||
      typeof message.turnToken !== "string" || message.turnToken.length > 128 ||
      typeof message.action !== "object" || message.action === null) return null;

  const action = message.action as Record<string, unknown>;
  const validCard = (card: unknown): card is Card =>
    typeof card === "number" && Number.isSafeInteger(card) && card >= 0 && card <= 255;
  let parsedAction: WireAction;
  if (action.type === "pass") parsedAction = { type: "pass" };
  else if (action.type === "returnTribute" && validCard(action.card)) {
    parsedAction = { type: "returnTribute", card: action.card };
  } else if (action.type === "play" && Array.isArray(action.cards) && action.cards.length > 0 &&
             action.cards.length <= 27 && action.cards.every(validCard)) {
    parsedAction = { type: "play", cards: [...action.cards] };
  } else return null;

  return {
    kind: "intent", id: message.id, expectSeq: message.expectSeq,
    turnToken: message.turnToken, action: parsedAction,
  };
}

export const REJECT_CODES = [
  "stale-seq",
  "stale-turn",
  "turn-not-ready",
  "not-your-turn",
  "wrong-phase",
  "no-cards",
  "cards-not-held",
  "not-a-combo",
  "cannot-beat",
  "cannot-pass-open-trick",
  "return-tribute-too-high",
  "bad-message",
  "rate-limited",
  "spectator",
] as const;
export type RejectCode = (typeof REJECT_CODES)[number];

export type ServerMessage =
  /**
   * Start fresh. Carries the authoritative snapshot, which the client adopts
   * wholesale.
   */
  | {
      kind: "hello";
      protocol: number;
      seat: Seat | null;
      room: RoomInfo;
      snapshot: Snapshot;
      /** Server time at send, for clock-offset estimation. */
      serverTime: number;
    }
  /**
   * Continue an existing model. Sent instead of `hello` when a reconnecting
   * client is close enough behind to be caught up by replaying events.
   *
   * These are different operations and must not be combined: a snapshot is the
   * state *now*, while replayed events describe the past. Applying the past on
   * top of the present rewinds whose turn it is, and the table stops dead.
   */
  | {
      kind: "resume";
      seat: Seat | null;
      room: RoomInfo;
      from: number;
      to: number;
      serverTime: number;
      /**
       * Where the table currently stands.
       *
       * The event log records transitions, not position. A client that missed
       * no events still needs this: if it disconnected while on turn, no event
       * was ever emitted to tell it so, and replaying an empty gap leaves it
       * waiting for a prompt that already happened.
       */
      turn: Turn;
    }
  /** Live seat metadata changed; this does not advance the game event sequence. */
  | { kind: "room"; room: RoomInfo }
  /** Private, durable receipts; resent on both cold and warm reconnect. */
  | { kind: "settlements"; hands: readonly import("./summary.js").HandSettlement[] }
  | { kind: "events"; events: readonly WireEvent[] }
  | { kind: "speech"; voiceSeq: number; startAt: number; endAt: number; cue: SpeechCue }
  | { kind: "hint"; seq: number; suggestions: readonly HintSuggestion[] }
  | ({ kind: "reaction" } & ReactionEvent)
  | {
      kind: "rejected";
      id: string;
      code: RejectCode;
      /** Plain-English reason, shown by the client's rules coach verbatim. */
      message: string;
      /** The server's current seq, so the client can tell if it is behind. */
      seq: number;
    }
  | { kind: "accepted"; id: string; seq: number }
  | { kind: "pong"; t: number; serverTime: number };

/** Client-side clock offset estimate from a ping/pong round trip. */
export function clockOffset(sentAt: number, serverTime: number, receivedAt: number): number {
  const roundTrip = receivedAt - sentAt;
  return serverTime + roundTrip / 2 - receivedAt;
}
